/**
 * TagDetailSheet [T123]
 *
 * Right-side drawer showing rich stats for a single tag:
 *  - Hero header with hashed color accent, Hash icon, tag name
 *  - This-month + all-time + total transaction stats (3-column grid)
 *  - 6-month spending AreaChart using tag color
 *  - Recent transactions (last 5, compact list)
 *  - Sticky footer: Filter | Rename
 */

import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart,
  Area,
  XAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { Hash, Filter, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import api from '@/services/api';

// ─── Tag color helper (mirrors Transactions.tsx getTagColor) ─────────────────

const TAG_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#14b8a6'];

function getTagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface TagStats {
  tag: string;
  count: number;
  totalAmount: number;
  thisMonth: { count: number; total: number };
  allTime: { count: number; total: number };
  last6Months: Array<{ month: string; amount: number }>;
  recentTransactions: Array<{
    id: string;
    description: string;
    amount: number;
    type: 'income' | 'expense' | 'transfer';
    date: string;
  }>;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface TagDetailSheetProps {
  tag: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRename?: (tag: string) => void;
  onFilterByTag?: (tag: string) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TagDetailSheet({
  tag,
  open,
  onOpenChange,
  onRename,
  onFilterByTag,
}: TagDetailSheetProps) {
  const { fmt } = useCurrency();
  const [stats, setStats] = useState<TagStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open || !tag) {
      setStats(null);
      return;
    }
    setIsLoading(true);
    api
      .get(`/transactions/tags/${encodeURIComponent(tag)}/stats`)
      .then((r) => setStats(r.data?.data ?? null))
      .catch(() => setStats(null))
      .finally(() => setIsLoading(false));
  }, [open, tag]);

  const color = tag ? getTagColor(tag) : '#6366f1';
  const gradientId = `tagGrad-${tag ?? 'x'}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[calc(100vw-3rem)] sm:max-w-[420px] flex flex-col p-0 gap-0 overflow-hidden"
      >
        {/* Hero header */}
        <div className="px-6 pt-8 pb-6" style={{ backgroundColor: `${color}18` }}>
          <SheetHeader className="space-y-3">
            <SheetDescription className="sr-only">Tag details</SheetDescription>
            <div className="flex items-center gap-4">
              <div
                className="h-12 w-12 rounded-full flex items-center justify-center shrink-0 border-2"
                style={{ backgroundColor: `${color}30`, borderColor: color }}
              >
                <Hash className="h-5 w-5" style={{ color }} />
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight truncate">
                  {tag ?? '…'}
                </SheetTitle>
                <Badge variant="secondary" className="mt-1">Tag</Badge>
              </div>
            </div>
          </SheetHeader>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="px-6 py-5 space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <div className="px-6 py-5 space-y-5">
              {/* Stats row: 3-column grid */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border px-2 py-3">
                  <p className="text-xs text-muted-foreground mb-0.5">This Month</p>
                  <p className="text-sm font-bold tabular-nums">{fmt(stats?.thisMonth.total ?? 0)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{stats?.thisMonth.count ?? 0} txns</p>
                </div>
                <div className="rounded-lg border px-2 py-3">
                  <p className="text-xs text-muted-foreground mb-0.5">All Time</p>
                  <p className="text-sm font-bold tabular-nums">{fmt(stats?.allTime.total ?? 0)}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{stats?.allTime.count ?? 0} txns</p>
                </div>
                <div className="rounded-lg border px-2 py-3">
                  <p className="text-xs text-muted-foreground mb-0.5">Transactions</p>
                  <p className="text-sm font-bold tabular-nums">{stats?.count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">total</p>
                </div>
              </div>

              {/* Area chart — last 6 months */}
              {stats?.last6Months && stats.last6Months.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Last 6 months</p>
                  <ResponsiveContainer width="100%" height={120}>
                    <AreaChart
                      data={stats.last6Months}
                      margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: string) => v.slice(5)}
                      />
                      <RechartsTooltip
                        formatter={(v: number) => [fmt(v), 'Total']}
                        labelFormatter={(l: string) => l}
                        contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke={color}
                        fill={`url(#${gradientId})`}
                        strokeWidth={2}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Recent transactions */}
              {stats?.recentTransactions && stats.recentTransactions.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Recent transactions</p>
                  <div className="space-y-2">
                    {stats.recentTransactions.map((txn) => (
                      <div
                        key={txn.id}
                        className="flex items-center justify-between gap-2 py-1.5 border-b border-border/40 last:border-0"
                      >
                        <span className="text-sm truncate flex-1">{txn.description}</span>
                        <span
                          className={cn(
                            'text-sm font-medium tabular-nums shrink-0',
                            txn.type === 'income'
                              ? 'text-green-600'
                              : txn.type === 'expense'
                                ? 'text-red-600'
                                : 'text-blue-600',
                          )}
                        >
                          {txn.type === 'expense' ? '-' : ''}{fmt(Math.abs(txn.amount))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isLoading && !stats && (
                <p className="text-sm text-center text-muted-foreground py-4">
                  No data available for this tag.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div className="border-t p-4 flex gap-2 shrink-0">
          <Button
            variant="outline"
            className="flex-1"
            disabled={!tag}
            onClick={() => {
              if (tag) {
                onFilterByTag?.(tag);
                onOpenChange(false);
              }
            }}
          >
            <Filter className="mr-2 h-4 w-4" /> Filter
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            disabled={!tag}
            onClick={() => {
              if (tag) {
                onRename?.(tag);
                onOpenChange(false);
              }
            }}
          >
            <Pencil className="mr-2 h-4 w-4" /> Rename
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
